/**
 * Загрузка медиа как сценарий: выбор файла → локальная проверка → подготовка →
 * PUT → complete → ожидание готовности.
 *
 * CC §8.6: в статусе `active` готовность приходит sync-событием и опрос
 * НЕ требуется; в `onboarding` sync недоступен (BS §5.7) — только опрос.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError } from '@shared/api/errors'
import type { LocalMediaDraft } from '@shared/db'
import type { Config, MediaLimit, UserStatus } from '@shared/api/types'
import {
  limitFor,
  checkDuration,
  checkSize,
  checkType,
  MODERATION_REASON_TEXT,
  type LocalRejection,
  type MediaPurpose,
} from '@shared/media/limits'
import { prepareImage, readMediaDuration } from '@shared/media/prepare-image'
import { pollUntilReady, UploadCancelled, uploadMedia, type UploadStage } from '@shared/media/upload'
import { syncEngine } from '@shared/sync/instance'

export interface MediaUploadState {
  stage: UploadStage | 'idle'
  /** 0..1 — доля переданных байтов на стадии `uploading`. */
  progress: number
  mediaId: string | null
  previewUrl: string | null
  previewBlob?: Blob | null
  /** Готовый к показу текст ошибки (U-14 / C-20). */
  error: string | null
  width?: number
  height?: number
  durationMs?: number | null
  contentType?: string
}

const IDLE: MediaUploadState = {
  stage: 'idle',
  progress: 0,
  mediaId: null,
  previewUrl: null,
  error: null,
}

interface Options {
  config: Config | null
  purpose: MediaPurpose
  /** Статус аккаунта решает способ ожидания готовности (CC §8.6). */
  accountStatus: UserStatus | undefined
}

export function useMediaUpload({ config, purpose, accountStatus }: Options) {
  const [state, setState] = useState<MediaUploadState>(IDLE)
  const abortRef = useRef<AbortController | null>(null)
  const previewRef = useRef<string | null>(null)

  const limit = limitFor(config, purpose)

  // Локальные object URL освобождаются, иначе они держат память до перезагрузки.
  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current)
      abortRef.current?.abort()
    }
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null

    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current)
      previewRef.current = null
    }

    setState(IDLE)
  }, [])

  /**
   * Ожидание готовности. В `active` — подписка на sync-событие; в `onboarding` —
   * опрос `GET /media/{mediaId}` (CC §8.6).
   *
   * Исход возвращается вызывающему: `cancelled` — ожидание прервано и результат
   * обработки неизвестен.
   */
  const awaitReady = useCallback(
    async (mediaId: string, signal: AbortSignal): Promise<'ready' | 'rejected' | 'cancelled'> => {
      const applyResult = (result: {
        state: 'ready' | 'rejected'
        rejectedReason?: string
      }): 'ready' | 'rejected' => {
        if (result.state === 'rejected') {
          setState((s) => ({
            ...s,
            stage: 'rejected',
            // Экраны строят черновик вложения по `mediaId` из состояния (C-11),
            // поэтому непригодный id убираем оттуда вместе с отказом.
            mediaId: null,
            error: MODERATION_REASON_TEXT[result.rejectedReason ?? 'other'] ?? 'Не прошло проверку',
          }))
          return 'rejected'
        }

        setState((s) => ({ ...s, stage: 'ready' }))
        return 'ready'
      }

      if (accountStatus === 'onboarding') {
        return applyResult(await pollUntilReady(mediaId, signal))
      }

      const engine = syncEngine()
      if (!engine) {
        // Движка нет (редкий случай гонки при входе) — падаем на опрос.
        return applyResult(await pollUntilReady(mediaId, signal))
      }

      // Событие могло прийти раньше подписки — забираем его из памяти движка.
      const earlier = engine.takeMediaResult(mediaId)
      if (earlier) return applyResult(earlier)

      return await new Promise<'ready' | 'rejected' | 'cancelled'>((resolve) => {
        const unsubscribe = engine.onMediaEvent((result) => {
          if (result.mediaId !== mediaId) return
          unsubscribe()

          resolve(applyResult(result))
        })

        signal.addEventListener(
          'abort',
          () => {
            unsubscribe()
            resolve('cancelled')
          },
          { once: true },
        )
      })
    },
    [accountStatus],
  )

  /** Загружает уже подготовленный blob (голосовое, видео). */
  const uploadBlob = useCallback(
    async (input: {
      blob: Blob
      purpose?: MediaPurpose
      contentType: string
      durationMs?: number | null
      previewUrl?: string
      width?: number
      height?: number
    }): Promise<string | null> => {
      const uploadPurpose = input.purpose ?? purpose
      if (!limitFor(config, uploadPurpose)) {
        setState({ ...IDLE, stage: 'failed', error: 'Конфигурация ещё не загружена' })
        return null
      }

      const controller = new AbortController()
      abortRef.current = controller

      if (input.previewUrl) previewRef.current = input.previewUrl

      setState({
        stage: 'preparing',
        progress: 0,
        mediaId: null,
        previewUrl: input.previewUrl ?? null,
        previewBlob: input.blob,
        error: null,
        width: input.width,
        height: input.height,
        durationMs: input.durationMs ?? null,
        contentType: input.contentType,
      })

      try {
        const mediaId = await uploadMedia({
          file: input.blob,
          contentType: input.contentType,
          purpose: uploadPurpose,
          durationMs: input.durationMs ?? null,
          signal: controller.signal,
          onProgress: (progress) =>
            setState((s) => ({ ...s, stage: progress.stage, progress: progress.progress })),
        })

        setState((s) => ({ ...s, mediaId, stage: 'processing' }))

        /*
         * Непрошедшее проверку вложение непригодно: отдать его id значило бы
         * приложить к сообщению или к профилю заведомо негодное медиа и получить
         * отказ сервера вместо понятной причины отказа (CC §8.6 п. 2).
         */
        if ((await awaitReady(mediaId, controller.signal)) !== 'ready') return null
        return mediaId
      } catch (error) {
        if (error instanceof UploadCancelled) {
          setState(IDLE)
          return null
        }

        setState((s) => ({ ...s, stage: 'failed', error: describeUploadError(error) }))
        return null
      } finally {
        abortRef.current = null
      }
    },
    [awaitReady, config, purpose],
  )

  /** Полный путь для файла из диалога выбора: проверка → подготовка → загрузка. */
  const uploadFile = useCallback(
    async (file: File): Promise<string | null> => {
      if (!limit) {
        setState({ ...IDLE, stage: 'failed', error: 'Конфигурация ещё не загружена' })
        return null
      }

      const rejection = await validate(file, limit, purpose)
      if (rejection) {
        setState({ ...IDLE, stage: 'failed', error: rejection.message })
        return null
      }

      if (purpose === 'profile_photo' || purpose === 'chat_image' || purpose === 'channel_photo') {
        const prepared = await prepareImage(file, limit, purpose)
        if (!prepared.ok) {
          setState({ ...IDLE, stage: 'failed', error: prepared.rejection.message })
          return null
        }

        return uploadBlob({
          blob: prepared.image.blob,
          contentType: prepared.image.contentType,
          previewUrl: prepared.image.previewUrl,
          width: prepared.image.width,
          height: prepared.image.height,
        })
      }

      // §10.2 п. 6: видео и голосовые не перекодируются.
      const durationMs = await readMediaDuration(file)
      return uploadBlob({
        blob: file,
        contentType: file.type,
        durationMs,
        previewUrl: URL.createObjectURL(file),
      })
    },
    [limit, purpose, uploadBlob],
  )

  /** Восстанавливает подготовленные байты и состояние после ухода из чата / перезапуска. */
  const restore = useCallback(
    async (draft: LocalMediaDraft): Promise<void> => {
      reset()
      const previewUrl = draft.preview_blob ? URL.createObjectURL(draft.preview_blob) : null
      previewRef.current = previewUrl
      if (!draft.media_id) {
        if (draft.preview_blob && draft.content_type) {
          await uploadBlob({
            blob: draft.preview_blob,
            contentType: draft.content_type,
            purpose: draft.content_type.startsWith('video/') ? 'chat_video' : 'chat_image',
            ...(previewUrl ? { previewUrl } : {}),
            durationMs: draft.duration_ms ?? null,
            ...(draft.width != null ? { width: draft.width } : {}),
            ...(draft.height != null ? { height: draft.height } : {}),
          })
        }
        return
      }
      setState({
        stage: draft.ready ? 'ready' : 'processing',
        progress: 1,
        mediaId: draft.media_id,
        previewUrl,
        previewBlob: draft.preview_blob ?? null,
        error: null,
        ...(draft.content_type ? { contentType: draft.content_type } : {}),
        ...(draft.width != null ? { width: draft.width } : {}),
        ...(draft.height != null ? { height: draft.height } : {}),
        durationMs: draft.duration_ms ?? null,
      })
      if (draft.ready) return
      // Событие media.ready могло прийти, пока приложение было закрыто.
      const controller = new AbortController()
      abortRef.current = controller
      try {
        const result = await pollUntilReady(draft.media_id, controller.signal)
        if (controller.signal.aborted) return
        setState((current) =>
          result.state === 'ready'
            ? { ...current, stage: 'ready' }
            : {
                ...current,
                stage: 'rejected',
                mediaId: null,
                error: MODERATION_REASON_TEXT[result.rejectedReason ?? 'other'] ?? 'Не прошло проверку',
              },
        )
      } catch (error) {
        if (!controller.signal.aborted)
          setState((current) => ({ ...current, stage: 'failed', error: describeUploadError(error) }))
      } finally {
        if (abortRef.current === controller) abortRef.current = null
      }
    },
    [reset, uploadBlob],
  )

  return { state, limit, uploadFile, uploadBlob, restore, reset, cancel: reset }
}

/* ===================================================================== */
/* Локальная проверка (CC §8.2 п. 4)                                      */
/* ===================================================================== */

async function validate(
  file: File,
  limit: MediaLimit,
  purpose: MediaPurpose,
): Promise<LocalRejection | null> {
  const typeRejection = checkType(limit, file)
  if (typeRejection) return typeRejection

  const sizeRejection = checkSize(limit, file.size)
  if (sizeRejection) return sizeRejection

  if (purpose === 'chat_video' || purpose === 'chat_voice') {
    const durationMs = await readMediaDuration(file)
    const durationRejection = checkDuration(limit, durationMs)
    if (durationRejection) return durationRejection
  }

  return null
}

/** CC §8.2 п. 5: серверные отказы — понятной причиной, а не кодом. */
function describeUploadError(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'payload_too_large':
        return 'Файл слишком большой'
      case 'unsupported_media_type':
        return 'Такой формат не поддерживается'
      case 'validation_failed':
        return 'Файл не прошёл проверку'
      case 'rate_limited':
        return 'Слишком много загрузок подряд. Попробуйте через минуту.'
      case 'conflict':
        return error.rateLimitReason() === 'upload_expired'
          ? 'Время загрузки истекло. Попробуйте ещё раз.'
          : 'Не удалось подтвердить загрузку. Попробуйте ещё раз.'
      default:
        return 'Не удалось загрузить файл'
    }
  }

  return 'Не удалось загрузить файл. Проверьте соединение.'
}
